/**
 * Session resolution helpers shared by better-auth middleware and route handlers.
 *
 * better-auth uses the __Secure- prefix automatically when BETTER_AUTH_URL is
 * https://, but fake requests can be built against localhost. Passing both
 * names keeps bearer-token session lookups stable across HTTP and HTTPS origins.
 */
export function sessionTokenToCookieHeader(sessionToken: string): string {
  return [
    `better-auth.session_token=${sessionToken}`,
    `__Secure-better-auth.session_token=${sessionToken}`,
  ].join("; ");
}

function decodeCookieToken(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value);
    return decoded || null;
  } catch {
    return null;
  }
}

function extractSessionTokenFromCookieHeader(cookieHeader: string): string | null {
  return extractSessionTokensFromCookieHeader(cookieHeader)[0] ?? null;
}

export function extractSessionTokensFromCookieHeader(cookieHeader: string): string[] {
  const tokens: string[] = [];
  const pattern = /(?:^|;\s*)(?:__Secure-)?better-auth\.session_token=([^;]+)/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(cookieHeader)) !== null) {
    const token = decodeCookieToken(match[1]);
    if (token) tokens.push(token);
  }
  return tokens;
}

export function buildSessionLookupHeaders(headers: Headers, cookieHeader: string): Headers {
  const sessionToken = extractSessionTokenFromCookieHeader(cookieHeader);
  if (!sessionToken) return headers;
  return buildSessionLookupHeadersForToken(headers, sessionToken);
}

export function buildSessionLookupHeadersForToken(headers: Headers, sessionToken: string): Headers {
  const nextHeaders = new Headers(headers);
  nextHeaders.set("cookie", sessionTokenToCookieHeader(sessionToken));
  return nextHeaders;
}

export function sessionCreatedAtMs(session: unknown): number {
  const value = (session as { session?: { createdAt?: unknown; created_at?: unknown } } | null | undefined)
    ?.session;
  const createdAt = value?.createdAt ?? value?.created_at;
  if (createdAt instanceof Date) return createdAt.getTime();
  if (typeof createdAt === "string" || typeof createdAt === "number") {
    const timestamp = new Date(createdAt).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }
  return 0;
}

export type SessionLookupResult = {
  session: any;
  sessionToken: string | null;
};

export type GetSession = (headers: Headers) => Promise<any>;

export type ResolveRequestSessionInput = {
  authorizationHeader?: string | null;
  cookieHeader?: string | null;
  headers: Headers;
  getSession: GetSession;
};

type ResolvedSessionCandidate = {
  session: any;
  sessionToken: string;
  index: number;
};

export async function resolveRequestSession(input: ResolveRequestSessionInput): Promise<SessionLookupResult> {
  if (input.authorizationHeader?.startsWith("Bearer ")) {
    const sessionToken = input.authorizationHeader.substring(7);
    const session = await input.getSession(new Headers({ cookie: sessionTokenToCookieHeader(sessionToken) }));
    return { session, sessionToken };
  }

  const cookieHeader = input.cookieHeader ?? "";
  const sessionTokens = [...new Set(extractSessionTokensFromCookieHeader(cookieHeader))];
  if (sessionTokens.length <= 1) {
    const session = await input.getSession(buildSessionLookupHeaders(input.headers, cookieHeader));
    return { session, sessionToken: sessionTokens[0] ?? null };
  }

  const resolved = await Promise.all(
    sessionTokens.map(async (sessionToken, index): Promise<ResolvedSessionCandidate | null> => {
      try {
        const session = await input.getSession(buildSessionLookupHeadersForToken(input.headers, sessionToken));
        return session ? { session, sessionToken, index } : null;
      } catch {
        return null;
      }
    }),
  );
  const valid = resolved.filter((candidate): candidate is ResolvedSessionCandidate => candidate !== null);
  if (valid.length === 0) return { session: null, sessionToken: null };

  valid.sort((a, b) => {
    const createdDelta = sessionCreatedAtMs(b.session) - sessionCreatedAtMs(a.session);
    return createdDelta || a.index - b.index;
  });
  const { session, sessionToken } = valid[0];
  return { session, sessionToken };
}
